import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Menu, X, UserRound } from "lucide-react";
import { useCart } from "@/lib/cart";
import { useSettings } from "@/lib/settings";
import { useContent } from "@/lib/content";
import { useCustomer } from "@/lib/customer";
import { scrollToEl } from "@/lib/smooth";

export default function Nav() {
  const { count, setOpen } = useCart();
  const settings = useSettings();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { customer } = useCustomer();

  const { t } = useContent();
  const links = [
    { label: t("nav.boutique"), to: "/boutique", testid: "nav-link-catalog" },
    { label: t("nav.services"), to: "/accompagnement", testid: "nav-link-services" },
    { label: t("nav.journal"), to: "/conseils", testid: "nav-link-blog" },
    { label: t("nav.contact"), hash: "#contact", testid: "nav-link-contact" },
  ];

  const goHash = (hash) => {
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate(`/${hash}`);
    } else {
      scrollToEl(hash);
    }
  };

  return (
    <>
      <header className={`fixed ${settings.banner_active ? "top-12 md:top-14" : "top-3 md:top-5"} left-1/2 -translate-x-1/2 w-[94%] max-w-6xl z-50 transition-[top] duration-300`}>
        <div className="bg-white/85 backdrop-blur-md border border-line rounded-full px-5 md:px-7 py-3 shadow-[0_8px_30px_rgba(16,114,105,0.08)] flex items-center justify-between">
          <Link to="/" data-testid="nav-link-home" className="flex items-center leading-none">
            <img src="/logo.png" alt={settings.shop_name} className="h-9 md:h-11 w-auto" />
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) =>
              l.hash ? (
                <button
                  key={l.label}
                  data-testid={l.testid}
                  onClick={() => goHash(l.hash)}
                  className="link-underline text-sm text-ink hover:text-teal transition-colors"
                >
                  {l.label}
                </button>
              ) : (
                <Link
                  key={l.label}
                  data-testid={l.testid}
                  to={l.to}
                  className="link-underline text-sm text-ink hover:text-teal transition-colors"
                >
                  {l.label}
                </Link>
              )
            )}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <Link
              to={customer ? "/compte" : "/compte/connexion"}
              data-testid="nav-account-link"
              aria-label={customer ? "Mon compte" : "Me connecter"}
              className={`flex items-center gap-2 rounded-full px-3 py-2 text-sm transition-colors ${customer ? "bg-mint/40 text-teal-dark hover:bg-mint/60" : "text-teal hover:bg-cream"}`}
            >
              <UserRound size={17} />
              <span className="hidden lg:inline">{customer ? (customer.name?.split(" ")[0] || "Mon compte") : "Mon compte"}</span>
            </Link>
            <button
              data-testid="nav-cart-trigger"
              onClick={() => setOpen(true)}
              className="relative flex items-center gap-2 rounded-full bg-teal text-cream px-4 py-2 text-sm font-medium hover:bg-teal-dark transition-colors active:scale-[0.98]"
            >
              <ShoppingBag size={16} />
              <span className="hidden sm:inline">{t("nav.cart")}</span>
              {count > 0 && (
                <span
                  data-testid="nav-cart-count"
                  className="absolute -top-1.5 -right-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-mint text-[11px] font-bold text-teal-dark px-1"
                >
                  {count}
                </span>
              )}
            </button>
            <button
              data-testid="nav-mobile-toggle"
              className="md:hidden text-teal p-1"
              onClick={() => setMobileOpen(true)}
              aria-label="Ouvrir le menu"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="nav-mobile-menu"
            className="fixed inset-0 z-[70] bg-teal text-cream flex flex-col"
            initial={{ opacity: 0, y: -24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.35 }}
          >
            <div className="flex items-center justify-between px-6 py-6">
              <img src="/logo-light.png" alt={settings.shop_name} className="h-12 w-auto" />
              <button data-testid="nav-mobile-close" onClick={() => setMobileOpen(false)} aria-label="Fermer le menu">
                <X size={30} />
              </button>
            </div>
            <nav className="flex flex-col gap-2 px-8 pt-10">
              {links.map((l, i) =>
                l.hash ? (
                  <button
                    key={l.label}
                    data-testid={l.testid}
                    onClick={() => goHash(l.hash)}
                    className="text-left font-serif text-4xl py-3 border-b border-cream/15"
                  >
                    {l.label}
                  </button>
                ) : (
                  <Link
                    key={l.label}
                    data-testid={l.testid}
                    to={l.to}
                    onClick={() => setMobileOpen(false)}
                    className="font-serif text-4xl py-3 border-b border-cream/15"
                  >
                    {l.label}
                  </Link>
                )
              )}
              <Link
                to={customer ? "/compte" : "/compte/connexion"}
                data-testid="nav-mobile-account-link"
                onClick={() => setMobileOpen(false)}
                className="font-serif text-4xl py-3 border-b border-cream/15 flex items-center gap-3"
              >
                <UserRound size={28} /> {customer ? "Mon compte" : "Me connecter"}
              </Link>
              <button
                data-testid="nav-mobile-close-btn"
                className="mt-8 self-start rounded-full bg-mint text-teal-dark px-8 py-3 font-medium"
                onClick={() => setMobileOpen(false)}
              >
                Fermer
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
